import type {
  CredentialDataApi,
  IAuthorizationApi,
  IAuthorizationCredentialApi,
  IGetAuthorizationCredentialsApiResponse,
} from '../types/Credentials.api';
import type { CredentialData, IAuthorization, IAuthorizationCredential } from '../types/Credentials';
import { AuthorizationStatus } from '../types/Credentials';

// TODO: Remove once API fully migrates 'pending' to 'pending_verification'
const normalizeStatus = (status: string | null): AuthorizationStatus | null => {
  if (!status) return null;
  if (status === 'pending') return AuthorizationStatus.PendingVerification;

  return status as AuthorizationStatus;
};

// TODO: Remove once API returns correct authorization status based on credentials
const getDerivedStatus = (
  status: AuthorizationStatus,
  credentials: IAuthorizationCredential[],
): AuthorizationStatus => {
  const requiredCreds = credentials.filter((c) => c.isRequired);
  if (
    requiredCreds.length > 0 &&
    requiredCreds.every((c) => c.status === AuthorizationStatus.Verified)
  ) {
    return AuthorizationStatus.Verified;
  }

  return status;
};

const toCredentialData = (data: CredentialDataApi | null): CredentialData | null => {
  if (!data) return null;

  if ('number' in data) {
    return {
      number: {
        credentialNumber: data.number.credential_number,
      },
    };
  }

  if ('npi' in data) {
    return {
      npi: {
        npiNumber: data.npi.npi_number,
      },
    };
  }

  if ('reference' in data) {
    return {
      reference: {
        officeName: data.reference.office_name,
        contactEmail: data.reference.contact_email,
        startDate: data.reference.start_date,
        endDate: data.reference.end_date,
      },
    };
  }

  if ('artifact' in data) {
    return {
      artifact: {
        files: data.artifact.files,
      },
    };
  }

  return null;
};

const toAuthorizationCredential = (api: IAuthorizationCredentialApi): IAuthorizationCredential => ({
  id: api.id,
  credentialName: api.credential_name,
  credential: api.credential,
  type: api.type,
  status: normalizeStatus(api.status as string | null),
  issuedAt: api.issued_at ? new Date(api.issued_at) : null,
  expiredAt: api.expired_at ? new Date(api.expired_at) : null,
  expiresSoon: api.expires_soon,
  isDraft: api.is_draft,
  isRequired: api.is_required,
  data: toCredentialData(api.data),
});

const toAuthorization = (api: IAuthorizationApi): IAuthorization => {
  const credentials = (api.credentials ?? []).map(toAuthorizationCredential);
  const normalizedStatus = normalizeStatus(api.status as string) as AuthorizationStatus;

  return {
    id: api.id,
    professionalId: api.professional_id,
    profession: api.profession,
    state: api.state,
    isAuthorized: api.is_authorized,
    status: getDerivedStatus(normalizedStatus, credentials),
    authorizationExpiresAt: api.authorization_expires_at
      ? new Date(api.authorization_expires_at)
      : null,
    credentials,
  };
};

const toAuthorizationList = (response: IGetAuthorizationCredentialsApiResponse): IAuthorization[] =>
  (response.authorizations ?? []).map(toAuthorization);

export const CredentialsAdapters = {
  toAuthorizationCredential,
  toAuthorization,
  toAuthorizationList,
};
